export const ROLE_LABELS = {
  admin: "Admin",
  yonetici: "Yönetici",
  muhasebeci: "Muhasebeci",
  muhasebe_stj: "Muhasebe Stj.",
  junior_muhasebe: "Junior Muhasebe",
  personel: "Personel",
};

export const ROLE_TONES = {
  admin: "bg-red-100 text-red-700",
  yonetici: "bg-violet-100 text-violet-700",
  muhasebeci: "bg-amber-100 text-amber-700",
  muhasebe_stj: "bg-teal-100 text-teal-700",
  junior_muhasebe: "bg-emerald-100 text-emerald-700",
  personel: "bg-blue-100 text-blue-700",
};

// Default (fallback) labels used before the backend `_labels` payload is loaded.
// Backend `/api/role-permissions` is the authoritative source and is merged over these.
export const DEFAULT_PERMISSION_LABELS = {
  canSeeMoney: "Para rakamlarını görebilir (sabit maaş, net, kesinti, yemek, yol vb.)",
  canEditMoney: "Para rakamlarını düzenleyebilir",
  canSeePrimRate: "Prim oran / ödenecek / kişi başı / araştırma primi görebilir",
  canSeeKidem: "Kıdem Primi / Kıdem Tazminatı bilgisini görebilir (bordroda kolon, personel listesinde butonlar)",
  canSeeTotals: "Bordro cetvelinin altındaki aylık toplam rakamları görebilir",
  canSeeKpiCards: "Üstteki KPI kartlarını (Aktif Personel, İzin+Rapor Günü, Net Maaş vb.) görebilir",
  canSeeTahsilatTotal: "Tahsilatlar ekranının üstündeki Bugün / Hafta / Ay / Toplam tahsilat tutarlarını görebilir",
  canSeeVekaletTotal: "Tahsilatlar ekranının üstündeki Bugün / Hafta / Ay / Toplam vekalet ücreti tutarlarını görebilir",
  canSeeReportVekalet: "Raporlar sekmesindeki Müvekkil Aylık Özet ve Tahsilat Trendleri raporlarında (önizleme, PDF, Excel) vekalet ücreti sütununu görebilir",
  canRejectPrim: "Pending primleri reddedebilir (personel'e bildirim gider)",
  canPreApprovePrim: "Pending primler için muhasebeci ön onayı verebilir (admin'in final onayı gerekir)",
};

// Short labels (used by audit log flag diff rows). Backend overrides at runtime.
export const DEFAULT_PERMISSION_LABELS_SHORT = {
  canSeeMoney: "Para rakamlarını görüntüleme",
  canEditMoney: "Para rakamlarını düzenleme",
  canSeePrimRate: "Prim oran/kişi başı/araştırma primi görüntüleme",
  canSeeKidem: "Kıdem Primi / Kıdem Tazminatı görüntüleme",
  canSeeTotals: "Bordro aylık toplamları görüntüleme",
  canSeeKpiCards: "Üst KPI kartlarını görüntüleme",
  canSeeTahsilatTotal: "Tahsilatlar özetinde tahsilat toplamlarını görüntüleme",
  canSeeVekaletTotal: "Tahsilatlar özetinde vekalet ücreti toplamlarını görüntüleme",
  canSeeReportVekalet: "Raporlar · Müvekkil Aylık Özet ve Tahsilat Trendleri'nde vekalet ücretini görüntüleme",
  canManageUsers: "Kullanıcı yönetimi",
  canRejectPrim: "Prim reddetme",
  canPreApprovePrim: "Prim ön onaylama",
  tabs: "Erişilebilir sekmeler",
};

export const DEFAULT_TAB_LABELS = {
  "tahsilat": "Tahsilatlar",
  "gider-avans": "Gider Avansları",
  "puantaj": "Puantaj",
  "prim-pending": "Onay Bekleyenler",
  "annual-leave": "Yıllık İzinler",
  "reports": "Raporlar",
  "creditors": "Müvekkiller",
  "payroll": "Bordro Cetveli",
  "prim": "Prim Hesaplama",
  "prim-summary": "Prim Özeti",
  "kidem-reserve": "Tazminat Rezervi",
  "personnel": "Personel",
  "advance": "Avans Yönetimi",
  "bildirim": "Bildirimler",
  "users": "Kullanıcılar",
};

// Backward-compat aliases (deprecated — prefer the module-level `labels` passed from UsersTab).
export const PERMISSION_LABELS = DEFAULT_PERMISSION_LABELS;
export const TAB_LABELS = DEFAULT_TAB_LABELS;

const OFFICE_TABS = ["tahsilat", "gider-avans", "payroll", "puantaj", "prim", "prim-pending", "personnel", "advance", "annual-leave", "reports"];
// Tabs each role's API access actually supports; others can't be granted from the matrix.
export const ROLE_TAB_SCOPE = {
  yonetici: OFFICE_TABS,
  muhasebeci: OFFICE_TABS,
  muhasebe_stj: ["tahsilat", "gider-avans"],
  junior_muhasebe: ["gider-avans"],
  personel: ["prim"],
};

export const ALL_TABS = ["tahsilat", "gider-avans", "payroll", "puantaj", "prim", "prim-pending", "prim-summary", "kidem-reserve", "personnel", "advance", "annual-leave", "reports", "bildirim", "creditors"];

/**
 * Merge backend `_labels` payload with local defaults so the UI survives missing keys.
 * Returns { roles, permissions, permissionsShort, tabs }.
 */
export function pickLabels(permsPayload) {
  const bl = (permsPayload && permsPayload._labels) || {};
  return {
    roles: { ...ROLE_LABELS, ...(bl.roles || {}) },
    permissions: { ...DEFAULT_PERMISSION_LABELS, ...(bl.permissions || {}) },
    permissionsShort: { ...DEFAULT_PERMISSION_LABELS_SHORT, ...(bl.permissionsShort || {}) },
    tabs: { ...DEFAULT_TAB_LABELS, ...(bl.tabs || {}) },
  };
}

export const ENTITY_LABELS = {
  personnel: "Personel",
  advance: "Avans",
  payroll: "Puantaj/Bordro",
  prim: "Prim",
  user: "Kullanıcı",
  role_permissions: "Rol İzinleri",
  impersonation: "Impersonation",
  settings: "Ayarlar",
  notification: "Bildirim",
};

export const ACTION_LABELS = {
  create: "Oluştur",
  update: "Güncelle",
  delete: "Sil",
  approve: "Onayla",
  dismiss: "Yoksay",
};

export const ACTION_TONES = {
  create: "bg-emerald-100 text-emerald-700",
  update: "bg-blue-100 text-blue-700",
  delete: "bg-rose-100 text-rose-700",
  approve: "bg-emerald-100 text-emerald-700",
  dismiss: "bg-slate-200 text-slate-700",
};

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("tr-TR", {
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export function toLocalInput(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function toIsoFromLocal(localDate, endOfDay = false) {
  if (!localDate) return "";
  const d = new Date(`${localDate}T${endOfDay ? "23:59:59" : "00:00:00"}`);
  return d.toISOString();
}

export function summarizeUA(ua) {
  if (!ua) return "—";
  const s = String(ua);
  let os = "Bilinmeyen cihaz";
  if (/iPhone|iPad|iPod/i.test(s)) os = "iOS";
  else if (/Android/i.test(s)) os = "Android";
  else if (/Windows/i.test(s)) os = "Windows";
  else if (/Macintosh|Mac OS X/i.test(s)) os = "Mac";
  else if (/Linux/i.test(s)) os = "Linux";
  let br = "";
  if (/Edg\//i.test(s)) br = "Edge";
  else if (/Chrome\//i.test(s)) br = "Chrome";
  else if (/Firefox\//i.test(s)) br = "Firefox";
  else if (/Safari\//i.test(s)) br = "Safari";
  return br ? `${os} · ${br}` : os;
}
